import { Device, DeviceStatusInfo, User } from "@fairfleet/geotab";
import PersonSharp from "@mui/icons-material/PersonSharp";
import Autocomplete from "@mui/material/Autocomplete";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { TFunction } from "i18next";
import { HtmlHTMLAttributes, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounced } from "../../hooks/useDebounced";
import { DeviceActivityIcon } from "@/components/DeviceActivityIcon";
import { useDevicesBySearch } from "@/hooks/devices/useDevicesBySearch";
import getDeviceLicensePlate from "@/hooks/getDeviceLicensePlate";
import { useDeviceActivityStatus } from "@/hooks/statusInfos/useDeviceActivityStatus";
import { useUsersBySearch } from "@/hooks/users/useUsersBySearch";
import { deviceActivity } from "@/utilities/deviceActivity";
import { getUserDisplayName } from "@/utilities/user/getUserDisplayName";

type Option = OptionDevice | OptionDriver;
type OptionDevice = { type: "device"; device: Device };
type OptionDriver = { type: "user"; driver: User };

interface Props {
  /** The selected {@link User}s. */
  users?: User[];
  /** The selected {@link Device}s. */
  devices?: Device[];
  /** Called when the selection changes. */
  onChange?(users?: User[], devices?: Device[]): void;
  onlyDrivers: boolean;
  showArchived: boolean;
  showArchivedVehicles?: boolean;
  showUsers?: boolean;
  deviceFields?: string[];
  /** Overrides the input label; defaults to the generic search label. */
  label?: string;
  /** Overrides the input placeholder; defaults to the generic driver-or-device hint. */
  placeholder?: string;
}

/**
 * A select input for selecting a {@link User} or a {@link Device}.
 */
export function DriverOrDeviceSelect({
  users: selectedUsers = [],
  devices: selectedDevices = [],
  onChange,
  onlyDrivers,
  showArchived,
  showArchivedVehicles,
  showUsers = true,
  deviceFields,
  label,
  placeholder,
}: Props) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const searchDebounced = useDebounced(search, 300);
  const { t } = useTranslation();
  const { data: drivers = [], isLoading: isDriversLoading } = useUsersBySearch(
    searchDebounced,
    onlyDrivers,
    showArchived,
    false,
    showUsers,
  );
  const { data: deviceData, isLoading: isDevicesLoading } = useDevicesBySearch(
    searchDebounced,
    showArchivedVehicles,
    deviceFields,
  );
  const devices = deviceData ?? [];

  const { statusInfoMap, isError: statusIsError } = useDeviceActivityStatus(deviceData, isOpen);

  const value = selectedUsers
    .map(driver => ({ type: "user", driver }) as Option)
    .concat(selectedDevices.map(device => ({ type: "device", device }) as Option));

  const selectedType = getSelectedType(value);
  // Options come from the server-side search (see filterOptions below); no client re-filtering.
  const options = [
    ...(showUsers ? drivers.map(driver => ({ type: "user", driver }) as OptionDriver) : []),
    ...devices.map(device => ({ type: "device", device }) as OptionDevice),
  ];

  function onOptionChange(_: unknown, value: Option[] | null) {
    const drivers = value?.filter((o): o is OptionDriver => o.type === "user").map(o => o.driver);
    const devices = value?.filter((o): o is OptionDevice => o.type === "device").map(o => o.device);

    onChange?.(drivers, devices);
  }

  return (
    <Autocomplete
      id="driver-or-device-select"
      size="small"
      multiple
      fullWidth
      value={value}
      loading={isDriversLoading || isDevicesLoading}
      autoComplete
      disableCloseOnSelect
      includeInputInList
      onChange={onOptionChange}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      limitTags={1}
      options={options}
      groupBy={option => groupBy(t, option)}
      getOptionLabel={option => (typeof option === "string" ? option : getOptionLabel(option))}
      isOptionEqualToValue={(option, selected) => {
        if (option.type !== selected.type) {
          return false;
        }

        if (option.type === "user") {
          return option.driver.id === (selected as OptionDriver).driver.id;
        }

        return option.device.id === (selected as OptionDevice).device.id;
      }}
      filterOptions={o => o}
      filterSelectedOptions
      renderOption={(props, option) => renderOption(props, option, statusInfoMap, statusIsError)}
      getOptionDisabled={option => (selectedType && option.type !== selectedType) ?? false}
      // input
      onInputChange={(_, value) => setSearch(value)}
      renderInput={params => (
        <TextField
          {...params}
          type="search"
          label={label ?? t("search.title")}
          fullWidth
          placeholder={placeholder ?? t("search.deviceOrDriver")}
        />
      )}
    />
  );
}

function getSelectedType(selected?: Option[]) {
  if (Array.isArray(selected)) {
    return selected[0]?.type;
  }
}

function getOptionLabel(option: Option) {
  if (option.type === "user") return getUserDisplayName(option.driver);
  if (option.type === "device") return option.device.name;

  return "";
}

function groupBy(t: TFunction, option: Option) {
  if (option.type === "user") return t("logbookEdit.driver");
  if (option.type === "device") return t("logbookEdit.device");

  return "";
}

function renderOption(
  props: HtmlHTMLAttributes<HTMLLIElement>,
  option: Option,
  statusInfoMap: Record<string, DeviceStatusInfo>,
  isError: boolean,
) {
  return (
    <li {...props} key={option.type === "user" ? option.driver.id : option.device.id}>
      {option.type === "user" && renderDriver(option)}
      {option.type === "device" && renderDevice(option, statusInfoMap[option.device.id], isError)}
    </li>
  );
}

const OptionStack = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-left: -16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function renderDriver(option: OptionDriver) {
  const isActive = new Date(option.driver.activeTo) >= new Date();
  return (
    <OptionStack>
      <PersonSharp style={{ color: isActive ? "inherit" : "gray" }} />
      {getUserDisplayName(option.driver)}
    </OptionStack>
  );
}

/**
 * A vehicle option: what it is doing right now, then its name.
 *
 * The same mark the live data page puts in front of a vehicle. Which vehicle is moving decides
 * whether its trips are still being written, and reading that off the list beats selecting a
 * vehicle to find out.
 */
function renderDevice(
  option: OptionDevice,
  status: DeviceStatusInfo | undefined,
  isError: boolean,
) {
  const isActive = new Date(option.device.activeTo) >= new Date();
  const licensePlate = getDeviceLicensePlate(option.device);

  return (
    <OptionStack>
      <DeviceActivityIcon activity={deviceActivity(status, isError)} />
      <span style={{ color: isActive ? "inherit" : "gray" }}>
        {option.device.name}
        {licensePlate ? ` · ${licensePlate}` : ""}
      </span>
    </OptionStack>
  );
}
